import { ChevronRight, NotebookPen } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { paperBackground, PAPER_UNIT, type PaperStyle } from "@/components/reader/paper";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, Tip } from "@/components/ui/overlays";
import { Input } from "@/components/ui/primitives";
import { keys } from "@/hooks/queries";
import { api, queryClient, type DocumentOut } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useGraph } from "@/stores/graph";
import { useTabs } from "@/stores/tabs";
import { useWritingEngine, type WritingEngine } from "@/stores/writing-engine";

// A few calm note-paper backgrounds; "Custom" opens the native color picker.
// Named, because "background #fffef7" is not something anyone can pick from —
// least of all a screen-reader user, for whom the swatch row WAS six hex codes.
const PRESETS: { hex: string; name: string }[] = [
  { hex: "#ffffff", name: "White" },
  { hex: "#fffef7", name: "Cream" },
  { hex: "#f3f7ff", name: "Ice" },
  { hex: "#f6fff2", name: "Mint" },
  { hex: "#fdf2f8", name: "Blush" },
  { hex: "#13160f", name: "Ink" },
];

// Page (a US-Letter notebook you add/remove pages from) vs. Infinite (one free
// surface you pan/zoom without page breaks — plan F7). Default Page.
type CanvasLayout = "pages" | "infinite";
const LAYOUTS: { value: CanvasLayout; label: string; blurb: string }[] = [
  { value: "pages", label: "Pages", blurb: "US Letter sheets you add to and page through" },
  { value: "infinite", label: "Infinite", blurb: "One boundless sheet you pan and zoom" },
];

// The paper style drawn behind the ink (pure CSS — plan §3.5). Default Blank.
const PAPERS: { value: PaperStyle; label: string }[] = [
  { value: "blank", label: "Blank" },
  { value: "ruled", label: "Ruled" },
  { value: "grid", label: "Grid" },
  { value: "dots", label: "Dot" },
];

/** A small picture of the note this choice makes.
 *
 * The layout and paper used to be two rows of text buttons, so "Page (US
 * Letter)" versus "Infinite" was a phrase to decode rather than a thing to
 * look at — and the paper colour, the pattern and the page shape were three
 * separate abstractions that only came together after the note existed. Each
 * tile now draws the actual paper: the chosen colour, the chosen pattern, and
 * the shape of the surface. Pages are sheets with a gap between them; infinite
 * bleeds past the frame, which is the whole difference.
 */
function LayoutPreview({
  layout,
  color,
  paper,
}: {
  layout: CanvasLayout;
  color: string;
  paper: PaperStyle;
}) {
  // A dense cell, so a pattern is legible inside a ~40px tile.
  const bg = paperBackground(paper, PAPER_UNIT / 5);
  const sheet = { backgroundColor: color, ...(bg ?? {}) };
  return (
    <span aria-hidden className="block h-[46px] w-full overflow-hidden rounded bg-background/40">
      {layout === "pages" ? (
        <span className="flex h-full flex-col items-center justify-center gap-[3px] py-1">
          <span className="h-[17px] w-[26px] rounded-[2px] shadow-sm ring-1 ring-black/15" style={sheet} />
          <span className="h-[17px] w-[26px] rounded-[2px] shadow-sm ring-1 ring-black/15" style={sheet} />
        </span>
      ) : (
        // No border and no margin: the paper runs off every edge, which is
        // exactly what an infinite surface does.
        <span className="block h-full w-full" style={sheet} />
      )}
    </span>
  );
}

/** Create a blank-canvas note (OneNote-style infinite surface, any color). */
export function BlankNoteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("#fffef7");
  // Which writing engine will OWN the note (persisted on it — a note always
  // reopens in the engine that wrote it). Seeded from the user's preference.
  const enginePref = useWritingEngine((s) => s.engine);
  // Seeded from the preference (classic by default). The scene editor is a
  // deliberate opt-in below, never the state you land in by accident.
  const [engine, setEngine] = useState<WritingEngine>(enginePref);
  const [layout, setLayout] = useState<CanvasLayout>("pages");
  const [paper, setPaper] = useState<PaperStyle>("blank");
  // Whether the user has picked a paper by hand. Until they do, the paper follows
  // the layout's natural default (infinite ⇒ Dot, its F7 signature look; pages ⇒
  // Blank) so an infinite note isn't born blank — but a manual choice is never
  // clobbered by a later layout switch.
  const [paperTouched, setPaperTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setColor("#fffef7");
      setEngine(useWritingEngine.getState().engine);
      setLayout("pages");
      setPaper("blank");
      setPaperTouched(false);
    }
  }, [open]);

  const chooseLayout = (value: CanvasLayout) => {
    setLayout(value);
    if (!paperTouched) setPaper(value === "infinite" ? "dots" : "blank");
  };

  const create = async () => {
    setBusy(true);
    try {
      const { document: doc } = await api.request<{ document: DocumentOut }>(
        "POST",
        "/api/documents/canvas",
        { title: title.trim() || "Blank note", background: color, engine, layout, paper },
      );
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      await queryClient.invalidateQueries({ queryKey: keys.documents });
      useTabs.getState().openReader(doc.id, doc.title);
      toast.success(`“${doc.title}” created`);
      onOpenChange(false);
      // Flash its node in the atlas.
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: keys.overview }), 300);
      useGraph.getState().requestFocus(doc.id);
    } catch (e) {
      toast.error("Could not create the note", { description: String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New handwritten note" className="w-[min(520px,92vw)]">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <NotebookPen className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              A handwritten note to draw and type on — the pen, highlighter, shapes and
              paint bucket, on real paper you pan and zoom. Choose a page notebook or an
              infinite surface; your handwriting is OCR’d in the background so the note
              stays searchable.
            </p>
          </div>
          <Input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Name… (e.g. Week 5 lecture)"
            aria-label="Note title"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Paper colour
            </span>
            {PRESETS.map(({ hex, name }) => (
              <Tip key={hex} label={name}>
                <button
                  type="button"
                  aria-label={`${name} paper`}
                  aria-pressed={color === hex}
                  onClick={() => setColor(hex)}
                  className={cn(
                    "h-6 w-6 rounded-full border-2 transition-transform hover:scale-110",
                    color === hex ? "border-primary" : "border-border",
                  )}
                  style={{ background: hex }}
                />
              </Tip>
            ))}
            <Tip label="Any other colour">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                aria-label="Custom paper colour"
                className="h-6 w-6 cursor-pointer appearance-none rounded-full border-2 border-dashed border-border bg-transparent p-0 [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-full [&::-webkit-color-swatch]:border-0"
              />
            </Tip>
          </div>
          {engine === "legacy" && (
          <>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Layout
            </span>
            <div
              className="mt-1.5 grid grid-cols-2 gap-2"
              role="radiogroup"
              aria-label="Handwritten note layout"
            >
              {LAYOUTS.map(({ value, label, blurb }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={layout === value}
                  onClick={() => chooseLayout(value)}
                  className={cn(
                    "rounded-lg border p-1.5 text-left transition-colors",
                    layout === value
                      ? "border-primary/70 bg-primary/10"
                      : "border-border bg-muted/30 hover:border-border/80 hover:bg-muted/50",
                  )}
                >
                  <LayoutPreview layout={value} color={color} paper={paper} />
                  <span
                    className={cn(
                      "mt-1.5 block px-0.5 text-[12px] font-medium",
                      layout === value ? "text-primary" : "text-foreground/85",
                    )}
                  >
                    {label}
                  </span>
                  <span className="block min-h-[2.5em] px-0.5 pb-0.5 text-[10.5px] leading-snug text-muted-foreground">
                    {blurb}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Paper
            </span>
            <div
              className="mt-1.5 grid grid-cols-4 gap-2"
              role="radiogroup"
              aria-label="Paper style"
            >
              {PAPERS.map(({ value, label }) => {
                // A live preview of the pattern on the CHOSEN background, so the
                // three decisions — colour, pattern, layout — are read together
                // rather than imagined separately.
                const bg = paperBackground(value, PAPER_UNIT / 5);
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={paper === value}
                    onClick={() => {
                      setPaper(value);
                      setPaperTouched(true);
                    }}
                    className={cn(
                      "rounded-lg border p-1.5 transition-colors",
                      paper === value
                        ? "border-primary/70 bg-primary/10"
                        : "border-border bg-muted/30 hover:border-border/80 hover:bg-muted/50",
                    )}
                  >
                    <span
                      aria-hidden
                      className="block h-7 w-full rounded-[3px] ring-1 ring-black/10"
                      style={{ backgroundColor: color, ...(bg ?? {}) }}
                    />
                    <span
                      className={cn(
                        "mt-1 block text-[11px]",
                        paper === value ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          </>
          )}
          {/* Excalidraw is deliberately out of the way. This dialog makes a
              NOTE — the pen, shapes and fill are what it is for — and the scene
              editor is a different thing kept for anyone who wants it, not a
              coin-flip presented alongside. So it lives in a collapsed
              disclosure, needs a second deliberate click, and says plainly what
              you give up; there is no toggle to fumble into on the way past. */}
          <details className="group rounded-lg border border-border/60 bg-muted/20">
            <summary className="cursor-pointer list-none px-3 py-2 text-[11px] text-muted-foreground transition-colors marker:content-[''] hover:text-foreground">
              <span className="inline-flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                Advanced — use the Excalidraw scene editor instead
              </span>
            </summary>
            <div className="space-y-2 border-t border-border/60 px-3 py-2.5">
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                A scene is a different editor: no pen toolbar, no paper styles, and none of
                the shape or fill tools. A note always reopens in the editor that made it,
                so this cannot be changed afterwards.
              </p>
              <label className="flex cursor-pointer items-start gap-2 text-[11px] text-foreground">
                <input
                  type="checkbox"
                  checked={engine === "excalidraw"}
                  onChange={(e) => setEngine(e.target.checked ? "excalidraw" : "legacy")}
                  className="mt-0.5 accent-[hsl(var(--primary))]"
                />
                Create this as an Excalidraw scene instead of a note
              </label>
            </div>
          </details>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={busy}>
              {busy
                ? "Creating…"
                : engine === "excalidraw"
                  ? "Create Excalidraw scene"
                  : "Create note"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
